// Shared constants for the AURA / CLEARpath current-loan flow (Dashboard, Qualify AURA and
// non-AURA paths, Submit, and the AURA polling helpers).
//
// One place for the option lists, limits, Encompass field IDs and Encompass value tables the
// current-loan screens use, grouped by the screen that uses them. Values are unchanged from
// where they were defined before; each group says what it is for.
//
// Not here on purpose:
//   - User-facing text → utils/verbiage.constants.js
//   - The Dashboard's per-section Encompass field-ID tables → utils/current-loan-encompass-field-ids.js
//   - CX.IncomeVerifyType and its value table → utils/income-verification-type.js
//   - The Step B form definition (labels, validation) → qualify/aura/productInputs.form.config.js
import {
	CLOSING_FIELD_IDS,
	CLOSING_VESTING_FIELD_IDS,
	CLOSING_READ_ONLY_FIELD_IDS,
	CLOSING_ENTITY_NAME_READ_ALT_ID,
} from "./current-loan-encompass-field-ids";
import { INCOME_VERIFY_TYPE_FIELD_ID } from "./income-verification-type";

// ────────────────────────────────────────────────────────────────────────────
// Encompass field IDs (current-loan flow)
// Every Encompass field the Qualify screens read or write directly, by name, so a field ID
// appears once. Used by qualify/aura (Step B product inputs save/read, Step A qualifying FICO,
// Step B DTI) and qualify/non-aura (prepayment penalty, stories on save).
// ────────────────────────────────────────────────────────────────────────────

export const ENCOMPASS_FIELD_IDS = {
	// Loan information
	INCOME_VERIFY_TYPE: INCOME_VERIFY_TYPE_FIELD_ID, // CX.IncomeVerifyType
	DTI: "742", // Bottom ratio (DTI) — calculated by Encompass, read-only on Step B
	DSCR: "CX.DEBTSERVICECOVERAGERATIO",
	LOAN_PURPOSE: "19",
	REFINANCE_CASH_OUT_TYPE: "URLA.X165", // NoCashOut / CashOut, written with the loan purpose
	PURCHASE_PRICE: "136",
	ESTIMATED_VALUE: "356",
	PROPERTY_VALUE: "1821", // purchase price on a purchase, estimated value on a refinance
	LOAN_AMOUNT: "1109",
	INTEREST_ONLY: "2982",
	PREPAYMENT_PENALTY: "CX.PrepaymentPenalty",
	// Written with CX.PrepaymentPenalty on save (same as create-loan-terms): both label fields get
	// the prepayment label ("1 Year"), or 0 when there is none; the months field gets the term.
	PREPAYMENT_PENALTY_LABEL_2964: "2964",
	PREPAYMENT_PENALTY_LABEL_2217: "2217",
	PREPAYMENT_PENALTY_MONTHS: "1947",
	ESCROW_WAIVER: "2293", // "Waived" / "Not Waived"
	ESCROW_WAIVER_INDICATOR: "MORNET.X15", // boolean, written with ESCROW_WAIVER
	// Borrower information
	CITIZENSHIP: "URLA.X1",
	FOREIGN_NATIONAL_BORROWER_1: "CX.FOREIGNNATLBORR1",
	FINANCED_PROPERTIES: "ULDD.TotalMortgagedPropertiesCount",
	RESERVES: "1548",
	// Credit profile
	QUALIFYING_FICO: "VASUMM.X23", // written by the credit vendor after a re-issue
	HOUSING_EVENT_TYPE: "CX.HOUSINGEVENTTYPE",
	HOUSING_EVENT_SEASONING: "CX.HOUSINGEVENTSEASONING",
	BANKRUPTCY_TYPE: "CX.BANKRUPTCYTYPE",
	BANKRUPTCY_OUTCOME: "CX.BANKRUPTCYOUTCOME",
	BANKRUPTCY_SEASONING: "CX.BANKRUPTCYSEASONING",
	MORTGAGE_LATE_ANY: "CX.MTGLATE.ANY", // "Y" / "N"; per-bucket counts are MORTGAGE_LATE_FIELD_IDS
	// Property information
	OCCUPANCY: "1811",
	PROPERTY_TYPE: "1041",
	SHORT_TERM_RENTAL: "CX.SHORT.TERM.RENTAL.YN",
	ZIP_CODE: "15",
	NUMBER_OF_UNITS: "16", // raw count; see UNIT_COUNT_FROM_ENUM
	NUMBER_OF_STORIES: "3245",
	// AURA run state (written by the backend's Run AURA / Sapiens) and the loan's condition model
	AUS_RESULT: "CX.AUS.RESULT", // AURA outcome; "In Progress" while Sapiens runs
	AUS_REQUEST_DT: "CX.AUS.REQUEST.DT", // run start, Eastern "MM-dd-yyyy hh:mm a"
	ENHANCED_CONDITIONS: "ENHANCEDCOND.X1", // "Y" = Enhanced Conditions; AURA is offered only then
};

// ────────────────────────────────────────────────────────────────────────────
// Qualify (AURA path) — Step B product inputs ↔ Encompass
// Used by pages/current-loan/qualify/aura (and the non-AURA page for the prepayment map).
// Labels shown for Encompass enum values, and the translation tables between the
// product-input form values and the strings Encompass stores.
// ────────────────────────────────────────────────────────────────────────────

// Display labels for occupancy values (form enum or Encompass value) on the Qualify summary.
export const OCCUPANCY_LABELS = {
	PrimaryResidence: "Primary Residence",
	Primary: "Primary Residence",
	SecondHome: "Second Home",
	Investor: "Investment Property",
	InvestmentProperty: "Investment Property",
};

// Display labels for property-type values on the Qualify summary.
export const PROPERTY_TYPE_LABELS = {
	SingleFamily: "Single Family",
	Detached: "Single Family",
	MultiFamily24: "Multi-Family 2–4 Unit",
	Condo: "Condo",
	PUD: "PUD",
	Manufactured: "Manufactured Home",
};

// Step B Property Type options are Optimal Blue codes (PROPERTY_TYPE in quickPricerConstants).
// The value can arrive as an Encompass 1041 value (Condominium, HighRiseCondominium, PUD),
// an XML gsePropertyType (Detached, Attached), a display label ("Single Family") or an OB
// code already — keyed here with case, spaces and punctuation stripped.
const PROPERTY_TYPE_OPTION_ALIASES = {
	singlefamily: "SingleFamily",
	detached: "SingleFamily",
	attached: "Townhouse",
	townhouse: "Townhouse",
	townhouserowhouse: "Townhouse",
	condo: "Condo",
	condominium: "Condo",
	highrisecondominium: "Condo",
	detachedcondominium: "Condo",
	condotel: "Condotel",
	nonwarrantablecondo: "NonWarrantableCondo",
	pud: "PUD",
	plannedunitdevelopment: "PUD",
};

// Property Type option value for any of the spellings above; "" when none fits
// (e.g. 2–4 unit, manufactured) so the broker picks it.
export const toPropertyTypeOption = (value) =>
	PROPERTY_TYPE_OPTION_ALIASES[String(value ?? "").toLowerCase().replace(/[^a-z]/g, "")] || "";

export const isPropertyTypeOption = (value) =>
	Boolean(value) && toPropertyTypeOption(value) === value;

// Step B Property Type → Encompass 1041, the values the wizard writes (create-loan-terms):
// condos are Condominium (HighRiseCondominium above 8 stories), PUD is PUD, and a
// single-family home or townhouse is left blank.
export const toEncompassPropertyType = (option, numberOfStories) => {
	if (["Condo", "Condotel", "NonWarrantableCondo"].includes(option)) {
		return Number(numberOfStories) > 8 ? "HighRiseCondominium" : "Condominium";
	}
	if (option === "PUD") return "PUD";
	if (option === "SingleFamily" || option === "Townhouse") return "";
	return undefined;
};

// Step B Occupancy options are PrimaryResidence / SecondHome / InvestmentProperty. XML and
// summary data carry Encompass values (Investor) or labels ("Investment Property").
const OCCUPANCY_OPTION_ALIASES = {
	primaryresidence: "PrimaryResidence",
	primary: "PrimaryResidence",
	secondhome: "SecondHome",
	secondary: "SecondHome",
	investmentproperty: "InvestmentProperty",
	investment: "InvestmentProperty",
	investor: "InvestmentProperty",
};

export const toOccupancyOption = (value) =>
	OCCUPANCY_OPTION_ALIASES[String(value ?? "").toLowerCase().replace(/[^a-z]/g, "")] || "";

export const isOccupancyOption = (value) => Boolean(value) && toOccupancyOption(value) === value;

// Encompass field values for CX.PrepaymentPenalty/2964/2217, confirmed by reading
// create-loan-terms/index.jsx's save logic — mapped from AURA's own enum values
// (PREPAYMENT_PENALTY_REQUIRED/OPTIONAL in utils/quickPricerConstants.js).
export const PREPAYMENT_PENALTY_FIELD_VALUES = {
	None: { label: "None", months: 0 },
	OneYear: { label: "1 Year", months: 12 },
	TwoYear: { label: "2 Year", months: 24 },
	ThreeYear: { label: "3 Year", months: 36 },
	FourYear: { label: "4 Year", months: 48 },
	FiveYear: { label: "5 Year", months: 60 },
};

// Encompass field IDs/value strings below are all confirmed by reading
// create-loan-terms/index.jsx's updateLoanFields save logic and utils/select-options.js —
// the actual wizard step OB reads this same loan data from. Panel B's own enum values
// (utils/quickPricerConstants.js) mostly don't match Encompass's expected strings 1:1, so
// each map below translates AURA's value to the one create-loan-terms confirms as correct.
// getEligibleProducts only forwards a handful of overrides (property/occupancy/citizenship/
// etc.) in its request body — everything else (income type, DTI inputs, loan purpose/amount,
// credit profile) is read by OB straight off the Encompass loan record, so it has to be saved
// here before the search runs or OB evaluates eligibility against stale/blank loan data.
// CX.IncomeVerifyType mapping lives in utils/income-verification-type.js (shared with
// Dashboard) — it previously used PLOneYear/PLTwoYear codes that don't exist in
// INCOME_VERIFICATION_TYPES, so saved P&L values never auto-selected.
export const LOAN_PURPOSE_ENCOMPASS_VALUES = {
	Purchase: "Purchase",
	RefiRateTermLimitedCO: "NoCash-Out Refinance",
	RefiCashout: "Cash-Out Refinance",
};

export const OCCUPANCY_ENCOMPASS_VALUES = {
	PrimaryResidence: "PrimaryResidence",
	SecondHome: "SecondHome",
	InvestmentProperty: "Investor",
};

export const CITIZENSHIP_ENCOMPASS_VALUES = {
	USCitizen: "USCitizen",
	PermResidentAlien: "PermanentResidentAlien",
	NonPermResidentAlien: "NonPermanentResidentAlien",
	ForeignNational: "Foreign National",
};

// Encompass field 16 ("units") is a raw count, but Panel B's numberOfUnits uses
// PROPERTY_UNITS' enum values (OneUnit/TwoUnits/…) — confirmed by reading Dashboard's own
// `property.units` (current-loan-encompass-field-ids.js PROPERTY_FIELD_IDS.units), which is
// set from `realProperty.financedNumberOfUnits` as a plain number string, not an enum.
export const UNIT_COUNT_FROM_ENUM = {
	OneUnit: "1", TwoUnits: "2", ThreeUnits: "3", FourUnits: "4",
	FiveUnits: "5", SixUnits: "6", SevenUnits: "7", EightUnits: "8",
};

// "Forbearance" (AURA's HOUSING_EVENT_TYPE) has no confirmed Encompass value and is left
// unmapped on purpose — see the module comment at the top of this block.
export const HOUSING_EVENT_ENCOMPASS_VALUES = {
	None: "None",
	DeedInLieu: "Deed-In-Lieu",
	Foreclosure: "Foreclosure",
	Modification: "Modification",
	ShortSale: "Short Sale",
};

// Shared by housingEventSeasoning and bankruptcySeasoning — both use the same option set.
export const SEASONING_ENCOMPASS_VALUES = {
	NotApplicable: "Not Applicable",
	Last1To12Months: "1 - 12 Months",
	Last13To24Months: "13 - 24 Months",
	Last25To36Months: "25 - 36 Months",
	Last37To48Months: "37 - 48 Months",
	Last49To60Months: "49 - 60 Months",
	MoreThanFiveYearsAgo: "More than Five Years Ago",
};

export const BANKRUPTCY_TYPE_ENCOMPASS_VALUES = {
	None: "None",
	Chapter7: "Chapter 7",
	Chapter11: "Chapter 11",
	Chapter13: "Chapter 13",
};

export const BANKRUPTCY_OUTCOME_ENCOMPASS_VALUES = {
	NotApplicable: "Not Applicable",
	Discharged: "Discharged",
	Dismissed: "Dismissed",
	OpenOrPending: "Open Or Pending",
};

// Encompass field ID for each Panel B mortgage-late bucket (CX.MTGLATE* — confirmed in
// create-loan-terms/index.jsx). Values are raw counts, written as-is.
export const MORTGAGE_LATE_FIELD_IDS = {
	Mtg_Lates_x30_12: "CX.MTGLATEX30MOS12",
	Mtg_Lates_x30_13_24: "CX.MTGLATEX30MOS13TO24",
	Mtg_Lates_x60_12: "CX.MTGLATEX60MOS12",
	Mtg_Lates_x60_13_24: "CX.MTGLATEX60MOS13TO24",
	Mtg_Lates_x90_12: "CX.MTGLATEX90MOS12",
	Mtg_Lates_x90_13_24: "CX.MTGLATEX90MOS13TO24",
	Mtg_Lates_x120_12: "CX.MTGLATEX120MOS12",
	Mtg_Lates_x120_13_24: "CX.MTGLATEX120MOS13TO24",
};

// Swaps an object's keys and values ({ a: "x" } → { x: "a" }). Builds the Encompass → form
// lookups below from the form → Encompass tables, so each pair is defined once.
export const invertMap = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));

// Encompass value → Step B form value, used when Step B hydrates from Encompass on open.
export const LOAN_PURPOSE_FROM_ENCOMPASS = invertMap(LOAN_PURPOSE_ENCOMPASS_VALUES);

export const OCCUPANCY_FROM_ENCOMPASS = invertMap(OCCUPANCY_ENCOMPASS_VALUES);

export const CITIZENSHIP_FROM_ENCOMPASS = invertMap(CITIZENSHIP_ENCOMPASS_VALUES);

export const HOUSING_EVENT_FROM_ENCOMPASS = invertMap(HOUSING_EVENT_ENCOMPASS_VALUES);

export const SEASONING_FROM_ENCOMPASS = invertMap(SEASONING_ENCOMPASS_VALUES);

export const BANKRUPTCY_TYPE_FROM_ENCOMPASS = invertMap(BANKRUPTCY_TYPE_ENCOMPASS_VALUES);

export const BANKRUPTCY_OUTCOME_FROM_ENCOMPASS = invertMap(BANKRUPTCY_OUTCOME_ENCOMPASS_VALUES);

export const UNIT_ENUM_FROM_COUNT = invertMap(UNIT_COUNT_FROM_ENUM);

// Encompass prepayment label ("1 Year") → Step B form value ("OneYear").
export const PREPAYMENT_PENALTY_FROM_LABEL = Object.fromEntries(
	Object.entries(PREPAYMENT_PENALTY_FIELD_VALUES).map(([key, v]) => [v.label, key])
);

// Every field ID buildProductInputsFieldValues writes — used to build the fetch-on-open
// field() request in one place so the two stay in sync.
export const PRODUCT_INPUTS_FIELD_IDS = [
	ENCOMPASS_FIELD_IDS.INCOME_VERIFY_TYPE,
	ENCOMPASS_FIELD_IDS.DSCR,
	ENCOMPASS_FIELD_IDS.LOAN_PURPOSE,
	ENCOMPASS_FIELD_IDS.PURCHASE_PRICE,
	ENCOMPASS_FIELD_IDS.ESTIMATED_VALUE,
	ENCOMPASS_FIELD_IDS.LOAN_AMOUNT,
	ENCOMPASS_FIELD_IDS.OCCUPANCY,
	ENCOMPASS_FIELD_IDS.CITIZENSHIP,
	ENCOMPASS_FIELD_IDS.FINANCED_PROPERTIES,
	ENCOMPASS_FIELD_IDS.RESERVES,
	ENCOMPASS_FIELD_IDS.INTEREST_ONLY,
	ENCOMPASS_FIELD_IDS.ESCROW_WAIVER,
	ENCOMPASS_FIELD_IDS.SHORT_TERM_RENTAL,
	ENCOMPASS_FIELD_IDS.ZIP_CODE,
	ENCOMPASS_FIELD_IDS.NUMBER_OF_UNITS,
	ENCOMPASS_FIELD_IDS.PROPERTY_TYPE,
	ENCOMPASS_FIELD_IDS.HOUSING_EVENT_TYPE,
	ENCOMPASS_FIELD_IDS.HOUSING_EVENT_SEASONING,
	ENCOMPASS_FIELD_IDS.BANKRUPTCY_TYPE,
	ENCOMPASS_FIELD_IDS.BANKRUPTCY_OUTCOME,
	ENCOMPASS_FIELD_IDS.BANKRUPTCY_SEASONING,
	ENCOMPASS_FIELD_IDS.MORTGAGE_LATE_ANY,
	ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY,
	ENCOMPASS_FIELD_IDS.NUMBER_OF_STORIES,
	...Object.values(MORTGAGE_LATE_FIELD_IDS),
	// Foreign National is URLA.X1 NonPermanentResidentAlien + this flag (see the write side).
	ENCOMPASS_FIELD_IDS.FOREIGN_NATIONAL_BORROWER_1,
];

// Credit vendors (esp. sandbox/test connections) can leave an order sitting at
// "Pending" indefinitely with no real bureau on the other end to process it —
// stop polling after ~2 minutes (60 x 2s) rather than spinning forever.
export const MAX_CREDIT_STATUS_POLL_ATTEMPTS = 60;

// ────────────────────────────────────────────────────────────────────────────
// Product inputs form
// Used by the Step B product-inputs form (productInputs.form.config.js, useProductInputsForm,
// ProductInputsPanel).
// ────────────────────────────────────────────────────────────────────────────

// Default values for every field — used to initialise Redux state and reset the form.
export const PRODUCT_INPUTS_DEFAULTS = {
	// Loan Information
	incomeVerificationType: "",
	debtToIncomeRatio: "",
	debtServiceCoverageRatio: "",
	loanPurpose: "",
	purchasePrice: "",
	estimatedValue: "",
	loanAmount: "",
	closedEndSecondLienAmt: "",
	helocLineAmount: "",
	helocDrawnAmount: "",
	cashoutAmount: "",
	ltv: "",
	cltv: "",
	hcltv: "",
	interestOnly: "",
	prePaymentPenalty: "",
	escrowWaiver: "",
	// Borrower Information
	citizenship: "",
	firstTimeHomeBuyer: "",
	nonoccupantCoborrower: "No",
	selfEmployed: "",
	reserve: "",
	financedProperties: "",
	// Credit Profile
	qualifyingCreditScore: "",
	housingEventType: "",
	housingEventSeasoning: "",
	bankruptcyType: "",
	bankruptcyOutcome: "",
	bankruptcySeasoning: "",
	mortgageLatePayments: "No",
	Mtg_Lates_x30_12: "",
	Mtg_Lates_x30_13_24: "",
	Mtg_Lates_x60_12: "",
	Mtg_Lates_x60_13_24: "",
	Mtg_Lates_x90_12: "",
	Mtg_Lates_x90_13_24: "",
	Mtg_Lates_x120_12: "",
	Mtg_Lates_x120_13_24: "",
	// Property Information
	propertyType: "",
	occupancy: "",
	shortTermRental: "",
	numberOfUnits: "",
	numberOfStories: "1",
	state: "",
	county: "",
	city: "",
	zipcode: "",
	// Product & Pricing Filters
	loanTerms: [],
	amortizationTypes: [],
	ARMFixedTerms: [],
	desiredRate: "",
	desiredPrice: 100.0,
	desiredLockPeriod: "30",
	lenderPaidvsBorrowerPaid: "",
	productTypes: ["StandardProducts", "ExpandedGuidelines"],
	loanType: ["Conforming", "NonConforming", "Conventional"],
	piLienType: "First",
};

// Quick Pricer field names that don't line up 1:1 with productInputs field names.
export const QUICK_PRICER_FIELD_MAP = {
	nonoccupanctCoborrower: "nonoccupantCoborrower",
	bankcruptcySeasoning: "bankruptcySeasoning",
	lienType: "piLienType",
	numberofUnits: "numberOfUnits",
	numberofStories: "numberOfStories",
};

// productInputs fields that must never be overwritten by Quick Pricer: auto-computed, or
// read from Encompass (DTI = field 742 on the saved loan, read-only in Step B).
export const QUICK_PRICER_SEED_SKIP = new Set(["ltv", "cltv", "hcltv", "debtToIncomeRatio"]);

// Form name passed to generateFieldId() for every Step B product-input field id.
export const PRODUCT_INPUTS_FORM_NAME = "productInputs";

// ────────────────────────────────────────────────────────────────────────────
// Qualify path selection
// Used by pages/current-loan/qualify/path-select: the steps listed on each path card.
// ────────────────────────────────────────────────────────────────────────────

// Steps listed on the "Run AURA" path card.
export const AURA_STEPS = [
	"A — Credit re-issue",
	"B — Product inputs",
	"C — Available programs",
	"D — AURA result + pricing",
];

// Steps listed on the manual (non-AURA) path card.
export const NON_AURA_STEPS = [
	"A — Product inputs (manual credit)",
	"B — Programs + pricing",
];

// ────────────────────────────────────────────────────────────────────────────
// Dashboard (full application form)
// Used by pages/current-loan/dashboard: select options, row limits, retry timing and the
// Encompass fields the Closing section reads. User-facing messages live in verbiage.constants.js.
// ────────────────────────────────────────────────────────────────────────────

// ── Options ───────────────────────────────────────────────────────────────────
// Every option list comes from src/utils/select-options.js — the same lists the Add New
// Loan wizard uses — so the values saved to Encompass match a wizard save exactly. The few
// lists below only re-key a wizard list whose values are booleans (SecondarySelect/
// CustomRadio need string values); the stored value is converted back on change.
export const BOOL_YES_NO_OPTIONS = [
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

// selfEmployedOptions (Employed=false / Self-Employed=true) with string values.
export const EMPLOYMENT_TYPE_OPTIONS = [
	{ value: "false", label: "Employed" },
	{ value: "true", label: "Self-Employed" },
];

// demographic-provided-information checkboxes (applicationTakenMethodType values).
export const APP_METHOD_OPTIONS = [
	{ value: "FaceToFace", label: "Face-to-Face Interview" },
	{ value: "Telephone", label: "Telephone Interview" },
	{ value: "Mail", label: "Fax or Mail" },
	{ value: "Internet", label: "Email or Internet" },
];

// Declarations A–M — same labels and keys as declaration-information/index.jsx.
export const DECLARATION_QUESTIONS = [
	["intentToOccupyIndicator", "A. Will you occupy the property as your primary residence?"],
	["specialBorrowerSellerRelationshipIndicator", "B. If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?"],
	["undisclosedBorrowedFundsIndicator", "C. Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor that you have not disclosed on this loan application?"],
	["undisclosedMortgageApplicationIndicator", "D.1 Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?"],
	["undisclosedCreditApplicationIndicator", "D.2 Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?"],
	["propertyProposedCleanEnergyLienIndicator", "E. Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?"],
	["undisclosedComakerOfNoteIndicator", "F. Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?"],
	["outstandingJudgementsIndicator", "G. Are there any outstanding judgements against you?"],
	["presentlyDelinquentIndicatorURLA", "H. Are you currently delinquent or in default on a federal debt?"],
	["partyToLawsuitIndicatorURLA", "I. Are you a party to a lawsuit in which you potentially have any personal financial liability?"],
	["priorPropertyDeedInLieuConveyedIndicator", "J. Have you conveyed title to any property in lieu of foreclosure in the past 7 years?"],
	["priorPropertyShortSaleCompletedIndicator", "K. Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?"],
	["priorPropertyForeclosureCompletedIndicator", "L. Have you had property foreclosed upon in the last 7 years?"],
	["bankruptcyIndicator", "M. Have you declared bankruptcy within the past 7 years?"],
];

// The portal supports up to three borrower pairs (CX.FOREIGNNATLBORR1-6 = 3 pairs × 2).
export const MAX_BORROWER_PAIRS = 3;

export const MAX_EMPLOYMENT_PER_OWNER = 3; // borrower-employment-information: "+" hidden at 3

export const MAX_OTHER_INCOME_ROWS = 10; // additional-income: max 10 rows

// Shared empty error set, so sections with no errors keep a stable reference between renders.
export const EMPTY_ERRORS = new Set();

// Wait before re-reading borrower pairs that Encompass has not returned yet after a save.
export const PAIR_READ_RETRY_MS = 1000;

// Dashboard sections saved per borrower pair.
export const PAIR_SECTIONS = ["borrower", "address", "employment", "assets", "declarations"];

// Snapshot marker for sections whose local state never reached a just-created loan (a creation
// step failed): they stay "changed" until saved, and reads don't overwrite them.
export const UNSYNCED_SNAPSHOT = "__unsynced__";

// ── Closing section — read-back field IDs ──────────────────────────────────────
// Same field list create-loan-fee reads on open (getFieldValues), so the Dashboard shows the
// saved Step 10 values and resolves the same visibility context (channel, compensation plan,
// occupancy, loan purpose, co-borrower name).
export const CLOSING_ALL_FIELD_IDS = [
	...Object.values(CLOSING_FIELD_IDS),
	CLOSING_VESTING_FIELD_IDS.individual.vestingType,
	CLOSING_VESTING_FIELD_IDS.individual.vestingWrittenAs,
	CLOSING_VESTING_FIELD_IDS.entity.vestingType,
	CLOSING_VESTING_FIELD_IDS.entity.vestingWrittenAs,
	CLOSING_ENTITY_NAME_READ_ALT_ID,
	...Object.values(CLOSING_READ_ONLY_FIELD_IDS),
];

// ────────────────────────────────────────────────────────────────────────────
// Current-loan Redux slice
// Used by features/current-loan/currentLoanSlice.
// ────────────────────────────────────────────────────────────────────────────

// localStorage key the current-loan progress (stage, inputs, results) is persisted under.
export const CURRENT_LOAN_STORAGE_KEY = "current-loan-progress";

// ────────────────────────────────────────────────────────────────────────────
// AURA / Optimal Blue polling
// Used by utils/aura-status-poll (pollUntil callers in Qualify Step C/D): poll intervals and
// time limits, and the patterns that classify a raw AURA result.
// ────────────────────────────────────────────────────────────────────────────

// AURA Run Process step 7a (rev. 10/21/2026): CX.AUS.RESULT is read every 15 s for up to
// 10 minutes; no result by then = the run failed (AURA_NO_RESULT).
export const AURA_POLL_INTERVAL_MS = 15000;
export const AURA_POLL_TIMEOUT_MS = 10 * 60 * 1000;

// In-progress lock (hooks/useAuraInProgress): while CX.AUS.RESULT is "In Progress" and the
// run started less than this many minutes ago, every current-loan write is locked. Mirrors the
// backend's DEFAULT_STALE_IN_PROGRESS_MINUTES (SAPIENS_STALE_IN_PROGRESS_MINUTES); keep in step.
export const AURA_RESULT_IN_PROGRESS = "In Progress";
export const AURA_STALE_IN_PROGRESS_MINUTES = 10; // client-confirmed 10 min (AURA Run Process rev. 10/21)
// How often a locked page re-reads the lock (kept at 10 s; separate from the 7a poll interval).
export const AURA_LOCK_RECHECK_INTERVAL_MS = 10000;

// Step C: how often the Optimal Blue registration queue is polled, and when to give up.
export const OB_QUEUE_POLL_INTERVAL_MS = 3000;
export const OB_QUEUE_POLL_TIMEOUT_MS = 5 * 60 * 1000;
// Optimal Blue answers 409 "Push is in queue." while an earlier push is still queued (clears in
// about a minute): wait and retry the attach / push, at most this many times.
export const OB_PUSH_IN_QUEUE_RETRY_MS = 15000;
export const OB_PUSH_IN_QUEUE_MAX_RETRIES = 8;

// AURA Run Process step 7b (rev. 10/21/2026): runs alongside 7a. The Encompass Workflow Rules
// engine may still be adding conditions, so CX.WRE.LASTRUN is read every 15 s; it is done once
// two reads in a row match (the second one after 7a has its result). At most 10 minutes, then
// the conditions are shown with a "may still be loading" notice (LC-AURA-005).
export const RULES_ENGINE_POLL_INTERVAL_MS = 15000;
export const RULES_ENGINE_POLL_TIMEOUT_MS = 10 * 60 * 1000;

// Maps the raw Sapiens CX.AUS.RESULT text onto Panel D's render states. Checks the
// negative words first because "ineligible" contains "eligible", and negated
// phrases ("Not Eligible", "Non-Approved", "Unapproved") contain the positive word.
export const AURA_RESULT_NEGATED_POSITIVE_RE = /\b(not|non|un)[\s-]*(elig|approv|accept|pass)/;

// Negative words in a raw AURA result (checked after the negated-positive pattern).
export const AURA_RESULT_NEGATIVE_RE = /(inelig|refer|declin|deny|denied|fail|unable|suspend)/;
